import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of, shareReplay, switchMap } from 'rxjs';
import {
  CyberKillChain,
  CyberSite,
  CyberSiteSummary,
} from '../models/cyber-site.model';
import { environment } from '../../environments/environment';

/**
 * Loads cyber site configurations and the kill-chain definition. Both are static per
 * deployment, so both are cached for the lifetime of the app.
 */
@Injectable({ providedIn: 'root' })
export class CyberSiteService {
  private http = inject(HttpClient);

  private readonly cyberApiUrl = `${environment.apiBaseUrl}/cyber`;

  private allSites$?: Observable<CyberSite[]>;
  private killChain$?: Observable<CyberKillChain>;

  /**
   * Every configured infrastructure, fully loaded. The page has no site picker — it draws
   * all of them at once — so the list endpoint only exists to discover the ids.
   */
  listAllSites(): Observable<CyberSite[]> {
    this.allSites$ ??= this.http
      .get<CyberSiteSummary[]>(`${this.cyberApiUrl}/sites`)
      .pipe(
        switchMap((summaries) =>
          summaries.length === 0
            ? of([])
            : forkJoin(
                summaries.map((summary) =>
                  this.http.get<CyberSite>(`${this.cyberApiUrl}/sites/${summary.id}`),
                ),
              ),
        ),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    return this.allSites$;
  }

  getKillChain(): Observable<CyberKillChain> {
    this.killChain$ ??= this.http
      .get<CyberKillChain>(`${this.cyberApiUrl}/killchain`)
      .pipe(shareReplay({ bufferSize: 1, refCount: false }));
    return this.killChain$;
  }
}
